// =============================================================================
// FILE: packages/renderer/src/components/public/RackDrafter.tsx
// PURPOSE: "On-Demand Rack Drafter" portal. A 3-step wizard: (1) build the
//   equipment list from the Parts Library, (2) add a title block + logo,
//   (3) preview the auto-drafted SVG elevation and download the full engineering
//   package directly from the customer inputs.
// =============================================================================

import { useMemo, useState, type JSX } from 'react';
import { X, Server, Plus, Minus, FileImage, Sparkles, Download, Check, ChevronRight, ChevronLeft, Cable, Tag, Network, Ruler, UploadCloud, FileSearch, AlertTriangle } from 'lucide-react';
import {
  RACK_LIBRARY, buildElevationSvg, generateRackPackage, estimateRackPackageSheetCount, DEFAULT_PROJECT_SCOPE,
  type Selection, type TitleBlock, type RackPackageResult, type ProjectScope,
} from '../../lib/rackDrafterGenerator.js';
import { evaluateCommercialAccess } from '../../lib/commercialControl.js';

const teal = '#25B5A8';
const rackFillColor = '#38BDF8';
const API_URL = (import.meta as any).env?.VITE_BACKEND_URL ?? (window as any).REACT_APP_BACKEND_URL ?? (typeof process !== 'undefined' ? (process.env as any)?.REACT_APP_BACKEND_URL ?? '' : '');

interface APSEEResult {
  job_id: string;
  filename: string;
  status: string;
  page_count: number;
  confidence: number;
  confidence_label: string;
  scope: ProjectScope & { devices?: number };
  extracted?: { rooms?: string[]; floors?: string[]; buildings?: string[]; racks?: string[]; riser_zones?: string[]; devices?: string[]; text_sample?: string };
  drawing_package?: { minimum_sheet_count?: number; estimated_sheet_count?: number; series?: string[] };
  warnings?: string[];
}

function MiniRackElevation({ totalRu }: { totalRu: number }): JSX.Element {
  const occupied: Array<{ start: number; size: number; label: string; color: string }> = [
    { start: 41, size: 2, label: 'ODF', color: '#818CF8' },
    { start: 39, size: 1, label: 'PP-01', color: '#38BDF8' },
    { start: 38, size: 1, label: 'CM', color: '#64748B' },
    { start: 37, size: 1, label: 'SW-01', color: '#2DD4BF' },
    { start: 36, size: 1, label: 'PP-02', color: '#38BDF8' },
    { start: 35, size: 1, label: 'CM', color: '#64748B' },
    { start: 34, size: 1, label: 'SW-02', color: '#2DD4BF' },
    { start: 31, size: 2, label: 'UPS', color: '#F59E0B' },
  ];
  const rows = Array.from({ length: 42 }, (_, i) => 42 - i);
  const used = Math.min(42, Math.max(totalRu, 11));

  return (
    <div data-testid="rd-first-view-rack-elevation" style={{ display: 'grid', gridTemplateColumns: '34px 1fr 68px', gap: 8, alignItems: 'stretch' }}>
      <div style={{ display: 'grid', gridTemplateRows: 'repeat(42, 8px)', gap: 1 }}>
        {rows.map((ru) => <div key={ru} style={{ fontFamily: 'monospace', fontSize: 7, color: ru % 2 === 0 ? 'rgba(148,163,184,0.7)' : 'rgba(148,163,184,0.35)', textAlign: 'right', lineHeight: '8px' }}>{ru}</div>)}
      </div>
      <div style={{ position: 'relative', display: 'grid', gridTemplateRows: 'repeat(42, 8px)', gap: 1, padding: 4, background: 'linear-gradient(180deg,#09111f,#050914)', border: `2px solid ${teal}66`, borderRadius: 8, boxShadow: `inset 0 0 0 1px ${teal}22, 0 18px 45px rgba(0,0,0,0.42)` }}>
        <div style={{ position: 'absolute', top: 8, bottom: 8, left: -8, width: 5, borderRadius: 999, background: 'linear-gradient(180deg,#94a3b8,#334155)' }} />
        <div style={{ position: 'absolute', top: 8, bottom: 8, right: -8, width: 5, borderRadius: 999, background: 'linear-gradient(180deg,#94a3b8,#334155)' }} />
        {rows.map((ru) => {
          const item = occupied.find(x => ru <= x.start && ru > x.start - x.size);
          const isPatch = item?.label.startsWith('PP');
          const isSwitch = item?.label.startsWith('SW');
          return (
            <div key={ru} style={{ position: 'relative', borderRadius: 2, border: '1px solid rgba(51,65,85,0.55)', background: item ? `${item.color}24` : 'rgba(15,23,42,0.82)', overflow: 'hidden' }}>
              {item && ru === item.start && <span style={{ position: 'absolute', left: 5, top: 0, color: '#e2e8f0', fontSize: 7, fontWeight: 900, lineHeight: '8px' }}>{item.label}</span>}
              {isPatch && Array.from({ length: 12 }).map((_, i) => <i key={i} style={{ position: 'absolute', right: 4 + i * 6, top: 2, width: 3, height: 3, borderRadius: 1, background: '#7dd3fc' }}/>) }
              {isSwitch && Array.from({ length: 8 }).map((_, i) => <i key={i} style={{ position: 'absolute', right: 5 + i * 8, top: 2, width: 5, height: 3, borderRadius: 1, background: i % 2 ? '#4ade80' : '#fbbf24' }}/>) }
            </div>
          );
        })}
        <svg aria-hidden style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} viewBox="0 0 230 388" preserveAspectRatio="none">
          <path d="M178 58 C218 78 218 116 178 138" fill="none" stroke="#38bdf8" strokeWidth="2" strokeDasharray="5 4" opacity="0.68"/>
          <path d="M178 82 C207 102 207 126 178 154" fill="none" stroke="#2dd4bf" strokeWidth="2" strokeDasharray="4 4" opacity="0.72"/>
          <path d="M44 226 C14 246 18 286 56 300" fill="none" stroke="#f59e0b" strokeWidth="2" strokeDasharray="5 5" opacity="0.68"/>
        </svg>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: 7 }}>
        {['MDF-01', `${used}U used`, 'TIA-606 labels', 'Cable paths'].map((label, i) => <div key={label} style={{ border: `1px solid ${i === 0 ? teal : 'rgba(148,163,184,0.22)'}`, borderRadius: 7, padding: '6px 7px', color: i === 0 ? '#5eead4' : 'rgba(226,232,240,0.74)', fontSize: 9.5, fontWeight: 800, background: i === 0 ? `${teal}12` : 'rgba(15,23,42,0.72)' }}>{label}</div>)}
      </div>
    </div>
  );
}

export function RackDrafter({ onClose }: { onClose: () => void }): JSX.Element {
  const [step, setStep] = useState(1);
  const [qty, setQty] = useState<Record<string, number>>({ odf: 1, panel48: 2, mgr: 2, switch48: 2, ups: 1, blank: 3 });
  const [tb, setTb] = useState<TitleBlock>({ projectName: 'Sierra Manor — MDF', client: 'Sierra Manor Dev Group', drawnBy: 'RCDD Engineer of Record', drawingNo: 'RCD2-RE-001' });
  const [scope, setScope] = useState<ProjectScope>(DEFAULT_PROJECT_SCOPE);
  const [apsee, setApsee] = useState<APSEEResult | null>(null);
  const [apseeFile, setApseeFile] = useState<File | null>(null);
  const [apseeBusy, setApseeBusy] = useState(false);
  const [apseePackageBusy, setApseePackageBusy] = useState<'pdf' | 'dxf' | 'archive' | null>(null);
  const [apseeError, setApseeError] = useState<string | null>(null);
  const [payMsg, setPayMsg] = useState<string | null>(null);
  const [pkg, setPkg] = useState<RackPackageResult | null>(null);
  const [busy, setBusy] = useState(false);
  const commercial = evaluateCommercialAccess('rack-maker');

  const selections: Selection[] = useMemo(
    () => RACK_LIBRARY.filter((it) => (qty[it.id] ?? 0) > 0).map((it) => ({ item: it, qty: qty[it.id]! })),
    [qty],
  );
  const sheetCount = useMemo(() => estimateRackPackageSheetCount(scope), [scope]);
  const svg = useMemo(() => (selections.length ? buildElevationSvg(selections, tb) : ''), [selections, tb]);
  const svgDataUrl = useMemo(() => (svg ? `data:image/svg+xml;utf8,${encodeURIComponent(svg)}` : ''), [svg]);

  const setItemQty = (id: string, delta: number) =>
    setQty((cur) => ({ ...cur, [id]: Math.max(0, Math.min(20, (cur[id] ?? 0) + delta)) }));

  const onLogo = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setTb((t) => ({ ...t, logoDataUrl: String(reader.result) }));
    reader.readAsDataURL(f);
  };

  const applyExtractedScope = (result: APSEEResult) => {
    setScope({
      buildings: result.scope.buildings,
      floors: result.scope.floors,
      telecomRooms: result.scope.telecomRooms,
      racks: result.scope.racks,
      riserZones: result.scope.riserZones,
      detailSheets: result.scope.detailSheets,
    });
    setTb((cur) => ({
      ...cur,
      projectName: result.filename.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ') || cur.projectName,
      drawingNo: `APSEE-${result.job_id.slice(0, 8).toUpperCase()}`,
    }));
    const rackCount = Math.max(1, Math.min(12, result.scope.racks));
    const roomCount = Math.max(1, Math.min(12, result.scope.telecomRooms));
    const deviceScale = Math.max(1, Math.ceil((result.scope.devices ?? 0) / 48));
    setQty({
      odf: Math.max(1, result.scope.riserZones),
      panel48: Math.max(2, Math.min(12, roomCount + deviceScale)),
      mgr: Math.max(2, Math.min(12, roomCount + rackCount)),
      switch48: Math.max(1, Math.min(10, deviceScale || rackCount)),
      ups: rackCount,
      blank: Math.max(2, Math.min(12, 42 - (roomCount + rackCount))),
    });
  };

  const extractScopeFromFile = async (file: File) => {
    setApseeBusy(true);
    setApseeError(null);
    setApseeFile(file);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch(`${API_URL}/api/r2-apsee/extract-scope`, { method: 'POST', body: form });
      const data = await response.json();
      if (!response.ok) throw new Error(data?.detail || 'APSEE extraction failed');
      setApsee(data);
      applyExtractedScope(data);
      setPayMsg(`APSEE extracted scope from ${data.filename} and auto-populated Rack Maker.`);
    } catch (err) {
      setApseeError(err instanceof Error ? err.message : 'APSEE extraction failed');
    } finally {
      setApseeBusy(false);
    }
  };

  const downloadApseePackage = async (format: 'pdf' | 'dxf' | 'archive') => {
    if (!apseeFile) return;
    setApseePackageBusy(format);
    setApseeError(null);
    try {
      const form = new FormData();
      form.append('file', apseeFile);
      const response = await fetch(`${API_URL}/api/r2-apsee/package/${format}`, { method: 'POST', body: form });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.detail || `APSEE ${format.toUpperCase()} package generation failed`);
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = format === 'archive' ? 'R2-APSEE-Telecom-Drawing-Package.zip' : `R2-APSEE-Telecom-Package.${format}`;
      a.click();
      URL.revokeObjectURL(url);
      setPayMsg(`APSEE production ${format === 'archive' ? 'archive' : format.toUpperCase()} package downloaded from uploaded project document.`);
    } catch (err) {
      setApseeError(err instanceof Error ? err.message : `APSEE ${format.toUpperCase()} package generation failed`);
    } finally {
      setApseePackageBusy(null);
    }
  };

  const onScopeUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) void extractScopeFromFile(file);
    e.target.value = '';
  };

  const generateAndDownload = async () => {
    setBusy(true);
    setPayMsg('Generating complete unwatermarked engineering package from your inputs…');
    const generated = await generateRackPackage(selections, tb, scope);
    setPkg(generated);
    setBusy(false);
    const link = document.createElement('a');
    link.href = generated.blobUrl;
    link.download = generated.fileName;
    link.click();
    setPayMsg('Complete unwatermarked engineering package downloaded.');
  };

  const download = () => {
    if (!pkg) return;
    const a = document.createElement('a');
    a.href = pkg.blobUrl; a.download = pkg.fileName; a.click();
  };

  const totalRu = selections.reduce((s, x) => s + x.item.ru * x.qty, 0);
  const setScopeValue = (key: keyof ProjectScope, value: string) => setScope((cur) => ({ ...cur, [key]: Math.max(key === 'detailSheets' ? 0 : 1, Math.min(120, Number(value) || 0)) }));

  return (
    <div data-testid="rack-drafter-modal" className="fixed inset-0 z-[10000] grid place-items-center p-4 overflow-y-auto" style={{ background: 'rgba(4,7,14,0.85)', backdropFilter: 'blur(8px)' }}>
      <div className="relative w-full max-w-6xl rounded-2xl my-6" style={{ background: 'linear-gradient(160deg,#0b1220,#0a0f1a)', border: `1px solid ${teal}44`, boxShadow: '0 30px 120px rgba(0,0,0,0.7)' }}>
        {/* header */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${teal}22` }}>
          <div className="flex items-center gap-3">
            <div className="grid place-items-center rounded-lg" style={{ width: 40, height: 40, background: `${teal}1f`, border: `1px solid ${teal}55` }}>
              <Server className="h-5 w-5" style={{ color: teal }} />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: `${teal}cc` }}>On-Demand Portal</div>
              <div className="text-base font-bold text-zinc-100">Rack Drafter · Certified Elevation Package</div>
            </div>
          </div>
          <button type="button" data-testid="rd-close" onClick={onClose} className="h-8 w-8 grid place-items-center rounded-md text-zinc-500 hover:text-zinc-200" style={{ border: '1px solid rgba(63,63,70,0.6)' }}>
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* stepper */}
        <div className="flex items-center gap-2 px-6 py-3 text-[11px]" style={{ borderBottom: '1px solid rgba(63,63,70,0.4)' }}>
          {['Preview Rack', 'Title Block', 'Download Package'].map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span data-testid={`rd-step-${i + 1}`} className="px-2.5 py-1 rounded-full font-semibold" style={{ background: step === i + 1 ? `${teal}22` : 'transparent', color: step >= i + 1 ? '#5eead4' : '#64748b', border: `1px solid ${step >= i + 1 ? teal + '55' : 'transparent'}` }}>
                {i + 1}. {s}
              </span>
              {i < 2 && <ChevronRight className="h-3 w-3 text-zinc-700" />}
            </div>
          ))}
        </div>

        <div className="p-6">
          {/* STEP 1 — Equipment */}
          {step === 1 && (
            <div data-testid="rd-step1-panel">
              <div className="grid grid-cols-1 lg:grid-cols-[0.95fr_1.05fr] gap-5">
                <div>
                  <div data-testid="rd-preview-first-headline" className="mb-4">
                    <div className="text-[10px] uppercase tracking-[0.24em] mb-2" style={{ color: `${teal}dd` }}>Professional deliverable preview</div>
                    <h2 className="text-2xl font-black text-zinc-50 leading-tight m-0">42U MDF rack elevation with equipment, labels, and cable paths.</h2>
                    <p className="text-[12.5px] text-zinc-400 leading-relaxed mt-3 mb-0">The first view shows the actual type of drawing the customer receives: RU numbering, rack naming, occupancy, patch panels, switches, cable management, and output sheets before any package activation.</p>
                  </div>
                  <MiniRackElevation totalRu={totalRu} />
                  <div data-testid="rd-example-output-summary" className="grid grid-cols-2 gap-2 mt-3">
                    {[
                      { icon: <Ruler className="h-3.5 w-3.5"/>, label: '42U numbered elevation' },
                      { icon: <Network className="h-3.5 w-3.5"/>, label: 'Patch panels + switches' },
                      { icon: <Cable className="h-3.5 w-3.5"/>, label: 'Cable management overlay' },
                      { icon: <Tag className="h-3.5 w-3.5"/>, label: 'TIA-606 labelling' },
                    ].map(item => <div key={item.label} className="flex items-center gap-2 rounded-lg px-3 py-2 text-[11px] font-semibold text-zinc-300" style={{ background: `${teal}0d`, border: `1px solid ${teal}22` }}>{item.icon}{item.label}</div>)}
                  </div>
                </div>
                <div>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <p className="text-[12px] text-zinc-400 m-0">Tune the equipment list and project scope. R2 stacks it into the 42U rack and scales the telecom package — <span className="text-teal-400 font-bold">{totalRu}U / 42U used · {sheetCount} sheets</span>.</p>
                    <div data-testid="rd-rack-fill-meter" className="w-28 h-2 rounded-full overflow-hidden bg-zinc-800 shrink-0"><div style={{ width: `${Math.min(100, (totalRu / 42) * 100)}%`, height: '100%', background: rackFillColor }} /></div>
                  </div>
                  <div data-testid="rd-apsee-upload-panel" className="mb-4 rounded-xl p-3" style={{ background: 'linear-gradient(135deg,rgba(37,181,168,0.12),rgba(56,189,248,0.06))', border: `1px solid ${teal}36` }}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em]" style={{ color: `${teal}dd` }}><FileSearch className="h-3.5 w-3.5" /> APSEE scope extraction</div>
                        <p data-testid="rd-apsee-description" className="text-[11.5px] text-zinc-300 leading-relaxed mt-1 mb-0">Upload PDF, IFC, schedules, documents, images, DWG, or RVT. R2 extracts buildings, floors, telecom rooms, racks, devices, risers, confidence, then pre-fills this rack package.</p>
                      </div>
                      <label data-testid="rd-apsee-upload-label" className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-sm text-[11px] font-black uppercase tracking-[0.12em] cursor-pointer shrink-0" style={{ background: teal, color: '#04110f' }}>
                        <UploadCloud className="h-4 w-4" /> {apseeBusy ? 'Analyzing…' : 'Upload Scope'}
                        <input data-testid="rd-apsee-file-input" type="file" accept=".pdf,.dxf,.ifc,.txt,.md,.csv,.xlsx,.docx,.png,.jpg,.jpeg,.webp,.dwg,.rvt,application/pdf,text/plain,text/csv,image/*" className="hidden" onChange={onScopeUpload} disabled={apseeBusy} />
                      </label>
                    </div>
                    {apseeError && <div data-testid="rd-apsee-error" className="mt-3 flex items-start gap-2 text-[11.5px] text-amber-200"><AlertTriangle className="h-3.5 w-3.5 mt-0.5" /> {apseeError}</div>}
                    {apsee && (
                      <div data-testid="rd-apsee-result" className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <div data-testid="rd-apsee-confidence" className="rounded-md px-2.5 py-2" style={{ background: 'rgba(4,8,16,0.55)', border: `1px solid ${teal}25` }}><span className="block text-[9px] uppercase tracking-[0.14em] text-zinc-500">Confidence</span><strong className="text-[13px] text-teal-300">{Math.round(apsee.confidence * 100)}% · {apsee.confidence_label}</strong></div>
                        <div data-testid="rd-apsee-pages" className="rounded-md px-2.5 py-2" style={{ background: 'rgba(4,8,16,0.55)', border: `1px solid ${teal}25` }}><span className="block text-[9px] uppercase tracking-[0.14em] text-zinc-500">Pages</span><strong className="text-[13px] text-zinc-100">{apsee.page_count}</strong></div>
                        <div data-testid="rd-apsee-devices" className="rounded-md px-2.5 py-2" style={{ background: 'rgba(4,8,16,0.55)', border: `1px solid ${teal}25` }}><span className="block text-[9px] uppercase tracking-[0.14em] text-zinc-500">Devices</span><strong className="text-[13px] text-zinc-100">{apsee.scope.devices ?? 0}</strong></div>
                        <div data-testid="rd-apsee-sheets" className="rounded-md px-2.5 py-2" style={{ background: 'rgba(4,8,16,0.55)', border: `1px solid ${teal}25` }}><span className="block text-[9px] uppercase tracking-[0.14em] text-zinc-500">Package</span><strong className="text-[13px] text-zinc-100">{apsee.drawing_package?.estimated_sheet_count ?? sheetCount} sheets</strong></div>
                      </div>
                    )}
                    {apsee && apseeFile && (
                      <div data-testid="rd-apsee-production-downloads" className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <button type="button" data-testid="rd-apsee-download-pdf" onClick={() => void downloadApseePackage('pdf')} disabled={!!apseePackageBusy} className="h-9 rounded-sm px-2 text-[10px] font-black uppercase tracking-[0.04em] disabled:opacity-50 whitespace-nowrap overflow-hidden text-ellipsis" style={{ background: 'rgba(37,181,168,0.18)', border: `1px solid ${teal}55`, color: '#99f6e4' }}>
                          {apseePackageBusy === 'pdf' ? 'Generating PDF…' : 'APSEE PDF'}
                        </button>
                        <button type="button" data-testid="rd-apsee-download-dxf" onClick={() => void downloadApseePackage('dxf')} disabled={!!apseePackageBusy} className="h-9 rounded-sm px-2 text-[10px] font-black uppercase tracking-[0.04em] disabled:opacity-50 whitespace-nowrap overflow-hidden text-ellipsis" style={{ background: 'rgba(56,189,248,0.14)', border: '1px solid rgba(56,189,248,0.45)', color: '#bae6fd' }}>
                          {apseePackageBusy === 'dxf' ? 'Generating DXF…' : 'APSEE DXF'}
                        </button>
                        <button type="button" data-testid="rd-apsee-download-archive" onClick={() => void downloadApseePackage('archive')} disabled={!!apseePackageBusy} className="h-9 rounded-sm px-2 text-[10px] font-black uppercase tracking-[0.04em] disabled:opacity-50 whitespace-nowrap overflow-hidden text-ellipsis" style={{ background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.45)', color: '#fde68a' }}>
                          {apseePackageBusy === 'archive' ? 'Generating ZIP…' : 'Complete ZIP'}
                        </button>
                      </div>
                    )}
                    {apsee?.warnings?.length ? <div data-testid="rd-apsee-warnings" className="mt-2 text-[10.5px] text-amber-200/90">{apsee.warnings[0]}</div> : null}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {RACK_LIBRARY.map((it) => (
                      <div key={it.id} data-testid={`rd-lib-${it.id}`} className="flex items-center justify-between rounded-lg px-3 py-2" style={{ background: 'rgba(12,20,40,0.6)', border: '1px solid rgba(63,63,70,0.5)' }}>
                        <div className="flex items-center gap-2 min-w-0">
                          <span style={{ width: 8, height: 8, borderRadius: 2, background: it.color, flexShrink: 0 }} />
                          <div className="min-w-0">
                            <div className="text-[12.5px] text-zinc-200 truncate">{it.label}</div>
                            <div className="text-[10px] text-zinc-500">{it.cat} · {it.ru}U</div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button type="button" data-testid={`rd-minus-${it.id}`} onClick={() => setItemQty(it.id, -1)} className="h-6 w-6 grid place-items-center rounded border border-zinc-700 text-zinc-400 hover:text-zinc-100"><Minus className="h-3 w-3" /></button>
                          <span data-testid={`rd-qty-${it.id}`} className="w-6 text-center text-[13px] text-zinc-100 font-mono">{qty[it.id] ?? 0}</span>
                          <button type="button" data-testid={`rd-plus-${it.id}`} onClick={() => setItemQty(it.id, 1)} className="h-6 w-6 grid place-items-center rounded border border-zinc-700 text-zinc-400 hover:text-zinc-100"><Plus className="h-3 w-3" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div data-testid="rd-scope-inputs" className="mt-4 rounded-xl p-3" style={{ background: 'rgba(8,14,26,0.58)', border: `1px solid ${teal}22` }}>
                    <div className="text-[10px] uppercase tracking-[0.18em] mb-2" style={{ color: `${teal}cc` }}>Project scope drives sheet count</div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {([
                        ['buildings', 'Buildings'], ['floors', 'Floors'], ['telecomRooms', 'Telecom rooms'], ['racks', 'Racks'], ['riserZones', 'Riser zones'], ['detailSheets', 'Detail sheets'],
                      ] as Array<[keyof ProjectScope, string]>).map(([key, label]) => (
                        <label key={key} className="block">
                          <span className="text-[9px] uppercase tracking-[0.12em] text-zinc-500 mb-1 block">{label}</span>
                          <input data-testid={`rd-scope-${key}`} type="number" min={key === 'detailSheets' ? 0 : 1} max={120} value={scope[key]} onChange={(e) => setScopeValue(key, e.target.value)} className="w-full h-9 bg-zinc-950/60 border border-zinc-800 rounded-sm px-2 text-[13px] text-zinc-100 focus:outline-none focus:border-teal-400/60" />
                        </label>
                      ))}
                    </div>
                    <div data-testid="rd-estimated-sheet-count" className="mt-2 text-[11.5px] text-zinc-300"><span style={{ color: '#6ee7b7', fontWeight: 800 }}>Estimated telecom package:</span> {sheetCount} sheets generated from current scope.</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* STEP 2 — Title block */}
          {step === 2 && (
            <div data-testid="rd-step2-panel" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {([['projectName', 'Project name'], ['client', 'Client'], ['drawnBy', 'Drawn by'], ['drawingNo', 'Drawing number']] as const).map(([k, label]) => (
                <label key={k} className="block">
                  <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">{label}</span>
                  <input data-testid={`rd-tb-${k}`} value={tb[k] as string} onChange={(e) => setTb((t) => ({ ...t, [k]: e.target.value }))}
                    className="w-full h-10 bg-zinc-900/60 border border-zinc-800 rounded-sm px-3 text-[13px] text-zinc-100 focus:outline-none focus:border-teal-400/60" />
                </label>
              ))}
              <label className="block md:col-span-2">
                <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">Title block logo (optional)</span>
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-700 text-zinc-300 text-[12px] cursor-pointer hover:border-zinc-600" data-testid="rd-logo-label">
                    <FileImage className="h-4 w-4" /> Upload logo
                    <input type="file" accept="image/*" className="hidden" data-testid="rd-logo-input" onChange={onLogo} />
                  </label>
                  {tb.logoDataUrl && <img src={tb.logoDataUrl} alt="logo" className="h-10 w-10 object-contain rounded border border-zinc-700" data-testid="rd-logo-preview" />}
                </div>
              </label>
            </div>
          )}

          {/* STEP 3 — Preview & download */}
          {step === 3 && (
            <div data-testid="rd-step3-panel">
              <div className="flex items-start gap-3 mb-4 rounded-xl p-3" style={{ background: `${teal}0f`, border: `1px solid ${teal}2b` }}>
                <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: teal }} />
                <p className="text-[12.5px] text-zinc-200 leading-relaxed m-0" data-testid="rd-emmy-msg">
                  <span className="font-semibold" style={{ color: teal }}>R²:</span> Here\u2019s your drafted elevation — {totalRu}U across {selections.length} equipment types with keynotes, rack naming, cable management, and general notes. Download the complete unwatermarked engineering package directly. Your current scope generates {sheetCount} sheets across {scope.buildings} building(s), {scope.floors} floor(s), {scope.telecomRooms} telecom room(s), {scope.racks} rack(s), and {scope.riserZones} riser zone(s).
                </p>
              </div>

              <div data-testid="rd-scalable-package-note" className="mb-3 rounded-lg px-3 py-2 text-[11.5px] leading-relaxed text-zinc-300" style={{ background: `${teal}0d`, border: `1px solid ${teal}2f` }}>
                <span className="font-bold" style={{ color: '#6ee7b7' }}>Scalable sheet logic:</span> T0.0 Cover/Notes · T1.x multi-floor floor plans · T2.x MDF/IDF/TR layouts · T3.x backbone risers · T4.x rack elevations · T5.x schedules{scope.detailSheets > 0 ? ' · T6.x details' : ''}. Actual sheet count expands for every level, area, room, rack, building, and riser path.
              </div>

              <div className="relative rounded-xl overflow-hidden mb-4" style={{ border: `1px solid ${teal}33`, background: '#0a0f1a' }}>
                {svgDataUrl && <img src={svgDataUrl} alt="Rack elevation preview" data-testid="rd-preview-img" className="w-full" style={{ aspectRatio: '1754/1240', objectFit: 'contain' }} />}
              </div>

              <div className="rounded-xl p-5" style={{ background: `${teal}12`, border: `1px solid ${teal}44` }}>
                <div className="flex items-center gap-2 mb-3 text-[13px]" style={{ color: '#6ee7b7' }} data-testid="rd-paid-confirm"><Check className="h-4 w-4" /> Full engineering package available — unwatermarked and ready to download.</div>
                <button type="button" data-testid="rd-download-btn" onClick={pkg ? download : generateAndDownload} disabled={busy || selections.length === 0} className="w-full h-11 rounded-lg font-bold text-[13px] uppercase tracking-[0.12em] flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: teal, color: '#04110f' }}>
                  <Download className="h-4 w-4" /> {busy ? 'Generating…' : pkg ? 'Download Package PDF Again' : 'Generate & Download Engineering Package'}
                </button>
              </div>
              {payMsg && <p className="text-[12px] text-zinc-400 mt-2" data-testid="rd-pay-msg">{payMsg}</p>}
            </div>
          )}
        </div>

        {/* footer nav */}
        <div className="flex items-center justify-between px-6 py-4" style={{ borderTop: '1px solid rgba(63,63,70,0.4)' }}>
          <button type="button" data-testid="rd-back" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-sm text-[12px] text-zinc-400 border border-zinc-700 disabled:opacity-30">
            <ChevronLeft className="h-3.5 w-3.5" /> Back
          </button>
          {step < 3 ? (
            <button type="button" data-testid="rd-next" onClick={() => setStep((s) => Math.min(3, s + 1))} disabled={selections.length === 0}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-sm text-[12px] font-bold disabled:opacity-40" style={{ background: teal, color: '#04110f' }}>
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : <span className="text-[11px] text-zinc-600">Certified by RCD² · ANSI/TIA & BICSI</span>}
        </div>
      </div>
    </div>
  );
}
